/**
 * 仮アイコン（プレースホルダー）生成。
 *
 * 設計方針:
 *   1. 円で統一しない。料理ごとに「器のシルエット」を変えて一目で見分けられるようにする。
 *      （茶碗 / 丼 / 鉄鍋 / 土鍋 / 角皿 / グラタン皿 / 折敷 / 食材そのもの）
 *   2. 中身は器の内側にクリップして描く。器からはみ出さない。
 *   3. 照り（ツヤ）と落ち影で「おいしそう」に見せる。
 *   4. シルエットは物理円（半径 r）の内側に収める。円は描かないので形の違いがそのまま見える。
 *
 * アクセシビリティ:
 *   色だけで区別させないため、必ず「器の形」「短縮文字」「レベル数字」を併用する。
 *
 * すべてオリジナルの手続き的描画であり、既存の画像・ロゴは一切使用しない。
 */

import type { IconShape, StageItemDefinition } from '../config/types';

const ICON_FONT =
  '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", "Yu Gothic", "Meiryo", sans-serif';

const TAU = Math.PI * 2;

/* ----------------------------- 色ユーティリティ ----------------------------- */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  const int = Number.parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(int)) return { r: 200, g: 200, b: 200 };
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function toCss({ r, g, b }: Rgb, alpha = 1): string {
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return alpha >= 1
    ? `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`
    : `rgba(${clamp(r)}, ${clamp(g)}, ${clamp(b)}, ${alpha})`;
}

function mix(hex: string, target: Rgb, amount: number): string {
  const base = parseHex(hex);
  return toCss({
    r: base.r + (target.r - base.r) * amount,
    g: base.g + (target.g - base.g) * amount,
    b: base.b + (target.b - base.b) * amount,
  });
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

export function lighten(hex: string, amount: number): string {
  return mix(hex, WHITE, amount);
}

export function darken(hex: string, amount: number): string {
  return mix(hex, BLACK, amount);
}

/** 背景色に対して読みやすい文字色を返す。 */
export function readableTextColor(hex: string): string {
  const { r, g, b } = parseHex(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#3a2419' : '#fffaf2';
}

/* ------------------------------ 描画の下ごしらえ ------------------------------ */

/**
 * 描画座標系。
 * すべての座標を「基準半径 r」の倍率で指定することで、
 * どのサイズで描いても同じ絵になり、物理円からのはみ出しも管理しやすい。
 */
interface Geo {
  /** キャンバス一辺 */
  s: number;
  cx: number;
  cy: number;
  /** 物理円に対応する基準半径 */
  r: number;
  /** 料理の主色 */
  base: string;
  /** 料理の差し色 */
  accent: string;
}

type Path = () => void;

function x(g: Geo, k: number): number {
  return g.cx + g.r * k;
}

function y(g: Geo, k: number): number {
  return g.cy + g.r * k;
}

function u(g: Geo, k: number): number {
  return g.r * k;
}

/** 同じアイコンが毎回同じ絵になるようにした簡易乱数。 */
function makeRandom(seed: number): () => number {
  let state = (Math.abs(Math.round(seed)) * 1103515245 + 12345) % 2147483647 || 42;
  return () => {
    state = (state * 1103515245 + 12345) % 2147483647;
    return state / 2147483647;
  };
}

function fillPath(ctx: CanvasRenderingContext2D, path: Path, style: string | CanvasGradient): void {
  ctx.beginPath();
  path();
  ctx.fillStyle = style;
  ctx.fill();
}

function strokePath(ctx: CanvasRenderingContext2D, path: Path, style: string, width: number): void {
  ctx.beginPath();
  path();
  ctx.strokeStyle = style;
  ctx.lineWidth = Math.max(0.6, width);
  ctx.stroke();
}

function clipped(ctx: CanvasRenderingContext2D, path: Path, draw: () => void): void {
  ctx.save();
  ctx.beginPath();
  path();
  ctx.clip();
  draw();
  ctx.restore();
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  rx: number,
  ry: number,
  w: number,
  h: number,
  radius: number,
): void {
  const rr = Math.min(radius, w / 2, h / 2);
  ctx.moveTo(rx + rr, ry);
  ctx.lineTo(rx + w - rr, ry);
  ctx.quadraticCurveTo(rx + w, ry, rx + w, ry + rr);
  ctx.lineTo(rx + w, ry + h - rr);
  ctx.quadraticCurveTo(rx + w, ry + h, rx + w - rr, ry + h);
  ctx.lineTo(rx + rr, ry + h);
  ctx.quadraticCurveTo(rx, ry + h, rx, ry + h - rr);
  ctx.lineTo(rx, ry + rr);
  ctx.quadraticCurveTo(rx, ry, rx + rr, ry);
  ctx.closePath();
}

/** 器の下の落ち影。地面に置かれているように見せる。 */
function groundShadow(ctx: CanvasRenderingContext2D, g: Geo, halfW: number, yk: number): void {
  fillPath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, yk), u(g, halfW), u(g, halfW * 0.16), 0, 0, TAU),
    'rgba(26, 12, 6, 0.32)',
  );
}

/** シルエットの外側に薄い影を落として、暗い背景から浮かせる。 */
function withDropShadow(ctx: CanvasRenderingContext2D, g: Geo, draw: () => void): void {
  ctx.save();
  ctx.shadowColor = 'rgba(18, 8, 4, 0.55)';
  ctx.shadowBlur = u(g, 0.14);
  ctx.shadowOffsetY = u(g, 0.05);
  draw();
  ctx.restore();
}

/* --------------------------------- 器の部品 --------------------------------- */

/** 椀・丼・鉢・鍋の共通仕様（数値はすべて r 単位）。 */
interface BowlSpec {
  /** 口の半径 */
  rimHalf: number;
  /** 口の高さ */
  rimY: number;
  /** 底の高さ */
  bottomY: number;
  /** 高台（底）の半径 */
  footHalf: number;
  /** 器の色 */
  color: string;
  /** 中身の盛り上がり高さ */
  moundH: number;
  /** 口の潰れ具合（楕円の縦横比） */
  flatten?: number;
  /** 縁の装飾帯の色 */
  bandColor?: string;
}

function bowlBody(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): Path {
  const rw = u(g, b.rimHalf);
  const ry = y(g, b.rimY);
  const by = y(g, b.bottomY);
  const fw = u(g, b.footHalf);
  const depth = by - ry;
  return () => {
    ctx.moveTo(g.cx - rw, ry);
    ctx.bezierCurveTo(g.cx - rw, ry + depth * 0.58, g.cx - fw * 1.35, by, g.cx - fw, by);
    ctx.lineTo(g.cx + fw, by);
    ctx.bezierCurveTo(g.cx + fw * 1.35, by, g.cx + rw, ry + depth * 0.58, g.cx + rw, ry);
    ctx.closePath();
  };
}

/** 中身を描くための内側クリップ（内縁の楕円＋盛り上がりのドーム）。 */
function bowlInner(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): Path {
  const rw = u(g, b.rimHalf) * 0.87;
  const ry = y(g, b.rimY);
  const rh = rw * (b.flatten ?? 0.26) * 0.86;
  const mh = u(g, b.moundH);
  return () => {
    ctx.moveTo(g.cx - rw, ry);
    ctx.bezierCurveTo(
      g.cx - rw * 0.9,
      ry - mh * 1.45,
      g.cx + rw * 0.9,
      ry - mh * 1.45,
      g.cx + rw,
      ry,
    );
    ctx.ellipse(g.cx, ry, rw, rh, 0, 0, Math.PI);
    ctx.closePath();
  };
}

/** 器の胴（陶器・鉄器の質感）。 */
function drawBowlBody(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): void {
  const ry = y(g, b.rimY);
  const by = y(g, b.bottomY);
  const body = bowlBody(ctx, g, b);

  const grad = ctx.createLinearGradient(0, ry, 0, by + u(g, 0.1));
  grad.addColorStop(0, lighten(b.color, 0.34));
  grad.addColorStop(0.38, lighten(b.color, 0.08));
  grad.addColorStop(1, darken(b.color, 0.42));

  withDropShadow(ctx, g, () => fillPath(ctx, body, grad));

  // 左からの光と右の陰
  clipped(ctx, body, () => {
    const light = ctx.createLinearGradient(x(g, -b.rimHalf), 0, x(g, b.rimHalf), 0);
    light.addColorStop(0, 'rgba(255, 246, 228, 0.26)');
    light.addColorStop(0.4, 'rgba(255, 255, 255, 0.04)');
    light.addColorStop(1, 'rgba(28, 14, 8, 0.28)');
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, g.s, g.s);

    if (b.bandColor) {
      ctx.fillStyle = b.bandColor;
      ctx.fillRect(0, ry + u(g, 0.06), g.s, u(g, 0.11));
    }
  });

  strokePath(ctx, body, darken(b.color, 0.6), u(g, 0.035));
}

/** 高台（器の足）。 */
function drawBowlFoot(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): void {
  const fw = u(g, b.footHalf * 0.96);
  const top = y(g, b.bottomY) - u(g, 0.02);
  const h = u(g, 0.12);
  fillPath(ctx, () => roundRectPath(ctx, g.cx - fw, top, fw * 2, h, h * 0.4), darken(b.color, 0.5));
}

/** 器の口（内縁のリング）。中身の上に重ねると「器に入っている」ように見える。 */
function drawBowlRim(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): void {
  const rw = u(g, b.rimHalf);
  const ry = y(g, b.rimY);
  const flat = b.flatten ?? 0.26;
  ctx.beginPath();
  ctx.ellipse(g.cx, ry, rw, rw * flat, 0, 0, TAU);
  ctx.ellipse(g.cx, ry, rw * 0.87, rw * flat * 0.86, 0, 0, TAU);
  const grad = ctx.createLinearGradient(0, ry - rw * flat, 0, ry + rw * flat);
  grad.addColorStop(0, lighten(b.color, 0.52));
  grad.addColorStop(1, darken(b.color, 0.18));
  ctx.fillStyle = grad;
  ctx.fill('evenodd');
}

/** 器の内側の影（中身の手前側を少し落とす）。 */
function drawInnerShade(ctx: CanvasRenderingContext2D, g: Geo, b: BowlSpec): void {
  clipped(ctx, bowlInner(ctx, g, b), () => {
    const ry = y(g, b.rimY);
    const grad = ctx.createLinearGradient(0, ry - u(g, b.moundH), 0, ry + u(g, 0.24));
    grad.addColorStop(0, 'rgba(30, 16, 8, 0)');
    grad.addColorStop(1, 'rgba(30, 16, 8, 0.4)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, g.s, g.s);
  });
}

/** 鍋の取手（両耳）。 */
function drawLugs(
  ctx: CanvasRenderingContext2D,
  g: Geo,
  b: BowlSpec,
  color: string,
  yk: number,
): void {
  const lw = u(g, 0.15);
  const lh = u(g, 0.11);
  const ly = y(g, yk);
  for (const side of [-1, 1]) {
    const lx = x(g, b.rimHalf * side) - (side < 0 ? lw * 0.82 : lw * 0.18);
    fillPath(ctx, () => roundRectPath(ctx, lx, ly, lw, lh, lh * 0.45), color);
    strokePath(
      ctx,
      () => roundRectPath(ctx, lx, ly, lw, lh, lh * 0.45),
      darken(color, 0.5),
      u(g, 0.025),
    );
  }
}

/** ツヤ。器と中身の上に薄く重ねて「照り」を出す。 */
function drawGlaze(ctx: CanvasRenderingContext2D, g: Geo, silhouette: Path): void {
  clipped(ctx, silhouette, () => {
    const gloss = ctx.createRadialGradient(
      x(g, -0.3),
      y(g, -0.42),
      u(g, 0.03),
      x(g, -0.18),
      y(g, -0.24),
      u(g, 1.02),
    );
    gloss.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
    gloss.addColorStop(0.4, 'rgba(255, 255, 255, 0.1)');
    gloss.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gloss;
    ctx.fillRect(0, 0, g.s, g.s);
  });
}

/** 小さな鋭いハイライト（濡れたようなツヤの主役）。 */
function drawSpecular(
  ctx: CanvasRenderingContext2D,
  g: Geo,
  xk: number,
  yk: number,
  rx: number,
  angle = -0.4,
  alpha = 0.72,
): void {
  fillPath(
    ctx,
    () => ctx.ellipse(x(g, xk), y(g, yk), u(g, rx), u(g, rx * 0.42), angle, 0, TAU),
    `rgba(255, 255, 255, ${alpha})`,
  );
}

/* ------------------------------- 料理ごとの絵 ------------------------------- */

type Drawer = (ctx: CanvasRenderingContext2D, g: Geo) => void;

/** 具材を散らす（クリップ済みの中で呼ぶ）。 */
function scatter(
  g: Geo,
  seed: number,
  count: number,
  area: { xk: number; yk: number; rx: number; ry: number },
  draw: (px: number, py: number, rnd: () => number) => void,
): void {
  const rnd = makeRandom(seed);
  for (let i = 0; i < count; i += 1) {
    const a = rnd() * TAU;
    const d = Math.sqrt(rnd());
    draw(x(g, area.xk + Math.cos(a) * area.rx * d), y(g, area.yk + Math.sin(a) * area.ry * d), rnd);
  }
}

const CERAMIC = '#f2ebdd';
const IRON = '#3b3a3e';
const CLAY = '#8b6a4f';
const LACQUER = '#8e2f26';
const INDIGO = '#2c4a72';

/* ---- L1 あまおう: 苺そのもの（一番よく出るので特に作り込む） ---- */
const drawBerry: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.46, 0.92);

  // 肩が張って先が丸く尖る、あまおう特有のシルエット
  const body: Path = () => {
    ctx.moveTo(x(g, 0), y(g, 0.9));
    ctx.bezierCurveTo(x(g, -0.34), y(g, 0.62), x(g, -0.72), y(g, 0.22), x(g, -0.74), y(g, -0.14));
    ctx.bezierCurveTo(x(g, -0.75), y(g, -0.42), x(g, -0.46), y(g, -0.56), x(g, -0.18), y(g, -0.54));
    ctx.bezierCurveTo(x(g, 0), y(g, -0.53), x(g, 0), y(g, -0.53), x(g, 0.18), y(g, -0.54));
    ctx.bezierCurveTo(x(g, 0.46), y(g, -0.56), x(g, 0.75), y(g, -0.42), x(g, 0.74), y(g, -0.14));
    ctx.bezierCurveTo(x(g, 0.72), y(g, 0.22), x(g, 0.34), y(g, 0.62), x(g, 0), y(g, 0.9));
    ctx.closePath();
  };

  // 深い紅から明るい朱へ。果肉の透明感を出すため中心をかなり明るくする
  const grad = ctx.createRadialGradient(
    x(g, -0.26),
    y(g, -0.22),
    u(g, 0.06),
    x(g, 0.08),
    y(g, 0.26),
    u(g, 1.15),
  );
  grad.addColorStop(0, lighten(g.base, 0.62));
  grad.addColorStop(0.34, lighten(g.base, 0.16));
  grad.addColorStop(0.72, g.base);
  grad.addColorStop(1, darken(g.base, 0.52));
  withDropShadow(ctx, g, () => fillPath(ctx, body, grad));

  clipped(ctx, body, () => {
    // 右下の落ち込みを強めて球体感を出す
    const shade = ctx.createRadialGradient(
      x(g, 0.42),
      y(g, 0.44),
      u(g, 0.1),
      x(g, 0.42),
      y(g, 0.44),
      u(g, 1.1),
    );
    shade.addColorStop(0, 'rgba(92, 10, 22, 0.34)');
    shade.addColorStop(1, 'rgba(92, 10, 22, 0)');
    ctx.fillStyle = shade;
    ctx.fillRect(0, 0, g.s, g.s);

    // 種。くぼみ（影）→ 種本体 → 上側の照り、の3層で立体的に見せる
    scatter(g, 11, 26, { xk: 0, yk: 0.12, rx: 0.56, ry: 0.6 }, (px, py, rnd) => {
      const sr = u(g, 0.032 + rnd() * 0.012);
      fillPath(
        ctx,
        () => ctx.ellipse(px, py + sr * 0.5, sr * 1.5, sr * 1.05, -0.5, 0, TAU),
        'rgba(108, 12, 26, 0.36)',
      );
      fillPath(
        ctx,
        () => ctx.ellipse(px, py, sr * 1.15, sr * 0.72, -0.5, 0, TAU),
        'rgba(255, 226, 140, 0.95)',
      );
      fillPath(
        ctx,
        () => ctx.ellipse(px - sr * 0.28, py - sr * 0.24, sr * 0.5, sr * 0.3, -0.5, 0, TAU),
        'rgba(255, 252, 226, 0.9)',
      );
    });
  });

  // ヘタ。細い棘ではなく幅のある葉にして、ひと目で苺と分かるようにする
  const leafDark = darken(g.accent, 0.26);
  const leafLight = lighten(g.accent, 0.34);
  const calyxBaseX = x(g, 0);
  const calyxBaseY = y(g, -0.3);
  for (let i = -2; i <= 2; i += 1) {
    const tilt = i * 0.46;
    const reach = 0.34 + (2 - Math.abs(i)) * 0.05;
    const tipX = calyxBaseX + u(g, Math.sin(tilt) * (reach + 0.16));
    const tipY = calyxBaseY - u(g, Math.cos(tilt) * (reach + 0.1));
    const angle = Math.atan2(tipY - calyxBaseY, tipX - calyxBaseX);
    const len = Math.hypot(tipX - calyxBaseX, tipY - calyxBaseY);
    const mx = (calyxBaseX + tipX) / 2;
    const my = (calyxBaseY + tipY) / 2;
    fillPath(
      ctx,
      () => ctx.ellipse(mx, my, len * 0.54, len * 0.29, angle, 0, TAU),
      i % 2 === 0 ? leafDark : g.accent,
    );
    // 葉脈の照り
    fillPath(
      ctx,
      () => ctx.ellipse(mx, my, len * 0.34, len * 0.07, angle, 0, TAU),
      'rgba(226, 255, 200, 0.28)',
    );
  }
  // 手前の一枚だけ明るくして重なりを見せる
  fillPath(
    ctx,
    () =>
      ctx.ellipse(
        calyxBaseX - u(g, 0.06),
        calyxBaseY - u(g, 0.2),
        u(g, 0.13),
        u(g, 0.24),
        -0.24,
        0,
        TAU,
      ),
    leafLight,
  );
  // ヘタの中心（軸）
  fillPath(
    ctx,
    () => ctx.ellipse(calyxBaseX, calyxBaseY - u(g, 0.03), u(g, 0.1), u(g, 0.075), 0, 0, TAU),
    darken(g.accent, 0.36),
  );

  // 濡れたようなハイライト（果物らしさの決め手）
  clipped(ctx, body, () => {
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, -0.3), y(g, -0.04), u(g, 0.15), u(g, 0.34), -0.34, 0, TAU),
      'rgba(255, 255, 255, 0.4)',
    );
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, -0.3), y(g, -0.06), u(g, 0.07), u(g, 0.2), -0.34, 0, TAU),
      'rgba(255, 255, 255, 0.6)',
    );
  });
  drawGlaze(ctx, g, body);
  drawSpecular(ctx, g, 0.26, -0.24, 0.11, -0.9, 0.62);
};

/* ---- L2 梅ヶ枝餅: 焼き餅（円盤に厚み） ---- */
const drawMochi: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.78, 0.72);

  const side = () => ctx.ellipse(g.cx, y(g, 0.22), u(g, 0.88), u(g, 0.5), 0, 0, TAU);
  const top = () => ctx.ellipse(g.cx, y(g, 0.0), u(g, 0.88), u(g, 0.5), 0, 0, TAU);

  withDropShadow(ctx, g, () => fillPath(ctx, side, darken(g.base, 0.4)));

  // 焼き上がりの香ばしい色。中央を白く、外周を狐色に
  const grad = ctx.createRadialGradient(
    x(g, -0.26),
    y(g, -0.2),
    u(g, 0.04),
    g.cx,
    y(g, 0.04),
    u(g, 1.05),
  );
  grad.addColorStop(0, lighten(g.base, 0.6));
  grad.addColorStop(0.45, lighten(g.base, 0.12));
  grad.addColorStop(0.82, darken(g.base, 0.1));
  grad.addColorStop(1, darken(g.base, 0.34));
  fillPath(ctx, top, grad);

  // 鉄板の焼き目（濃淡のある斑）
  clipped(ctx, top, () => {
    scatter(g, 23, 14, { xk: 0, yk: 0.02, rx: 0.62, ry: 0.32 }, (px, py, rnd) => {
      fillPath(
        ctx,
        () =>
          ctx.ellipse(
            px,
            py,
            u(g, 0.09 + rnd() * 0.14),
            u(g, 0.04 + rnd() * 0.05),
            rnd() * TAU,
            0,
            TAU,
          ),
        `rgba(158, 92, 34, ${0.16 + rnd() * 0.3})`,
      );
    });
    // 縁の焦げ
    strokePath(
      ctx,
      () => ctx.ellipse(g.cx, y(g, 0.0), u(g, 0.8), u(g, 0.44), 0, 0, TAU),
      'rgba(126, 66, 22, 0.28)',
      u(g, 0.1),
    );
  });

  // 梅の刻印（焼き印なので少し焦がした色で）
  const stamp = darken(g.accent, 0.18);
  for (let i = 0; i < 5; i += 1) {
    const a = -Math.PI / 2 + (i / 5) * TAU;
    fillPath(
      ctx,
      () =>
        ctx.ellipse(
          x(g, Math.cos(a) * 0.2),
          y(g, 0.02 + Math.sin(a) * 0.12),
          u(g, 0.12),
          u(g, 0.075),
          a,
          0,
          TAU,
        ),
      stamp,
    );
    // 刻印の上側に光を入れて凹凸を出す
    fillPath(
      ctx,
      () =>
        ctx.ellipse(
          x(g, Math.cos(a) * 0.2),
          y(g, -0.005 + Math.sin(a) * 0.12),
          u(g, 0.09),
          u(g, 0.045),
          a,
          0,
          TAU,
        ),
      'rgba(255, 234, 196, 0.3)',
    );
  }
  fillPath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, 0.02), u(g, 0.075), u(g, 0.05), 0, 0, TAU),
    lighten(g.accent, 0.42),
  );

  strokePath(ctx, top, darken(g.base, 0.36), u(g, 0.03));
  drawGlaze(ctx, g, top);
  drawSpecular(ctx, g, -0.32, -0.22, 0.26, -0.28, 0.5);
};

/* ---- L3 明太子: 笹の葉に明太子2本 ---- */
const drawRoe: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.82, 0.68);

  // 笹の葉
  const leafColor = '#4f7a3a';
  fillPath(
    ctx,
    () => {
      ctx.moveTo(x(g, -0.92), y(g, 0.34));
      ctx.quadraticCurveTo(x(g, 0), y(g, -0.2), x(g, 0.92), y(g, 0.18));
      ctx.quadraticCurveTo(x(g, 0), y(g, 0.6), x(g, -0.92), y(g, 0.34));
      ctx.closePath();
    },
    leafColor,
  );
  strokePath(
    ctx,
    () => {
      ctx.moveTo(x(g, -0.86), y(g, 0.33));
      ctx.quadraticCurveTo(x(g, 0), y(g, 0.16), x(g, 0.88), y(g, 0.2));
    },
    'rgba(255,255,255,0.35)',
    u(g, 0.02),
  );

  // 明太子（紡錘形）を2本
  const roe = (offX: number, offY: number, angle: number, scale: number, seed: number) => {
    const path: Path = () => {
      ctx.save();
      ctx.translate(x(g, offX), y(g, offY));
      ctx.rotate(angle);
      ctx.scale(scale, scale);
      ctx.moveTo(u(g, -0.74), 0);
      ctx.bezierCurveTo(u(g, -0.4), u(g, -0.3), u(g, 0.4), u(g, -0.28), u(g, 0.74), u(g, -0.02));
      ctx.bezierCurveTo(u(g, 0.42), u(g, 0.28), u(g, -0.4), u(g, 0.3), u(g, -0.74), 0);
      ctx.closePath();
      ctx.restore();
    };
    const grad = ctx.createLinearGradient(
      x(g, offX),
      y(g, offY - 0.24 * scale),
      x(g, offX),
      y(g, offY + 0.24 * scale),
    );
    grad.addColorStop(0, lighten(g.base, 0.42));
    grad.addColorStop(0.5, g.base);
    grad.addColorStop(1, darken(g.accent, 0.18));
    withDropShadow(ctx, g, () => fillPath(ctx, path, grad));

    clipped(ctx, path, () => {
      scatter(
        g,
        seed,
        34,
        { xk: offX, yk: offY, rx: 0.5 * scale, ry: 0.16 * scale },
        (px, py, rnd) => {
          fillPath(
            ctx,
            () => ctx.arc(px, py, u(g, 0.02 + rnd() * 0.014), 0, TAU),
            `rgba(255, 226, 214, ${0.24 + rnd() * 0.4})`,
          );
        },
      );
      // 薄皮の筋
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, offX - 0.6 * scale), y(g, offY - 0.06 * scale));
          ctx.quadraticCurveTo(
            x(g, offX),
            y(g, offY - 0.2 * scale),
            x(g, offX + 0.6 * scale),
            y(g, offY - 0.04 * scale),
          );
        },
        'rgba(255, 255, 255, 0.32)',
        u(g, 0.03),
      );
    });
    drawSpecular(ctx, g, offX - 0.2 * scale, offY - 0.12 * scale, 0.18 * scale, angle - 0.2, 0.55);
  };

  roe(-0.06, 0.02, -0.2, 0.95, 31);
  roe(0.1, 0.34, -0.12, 0.8, 47);

  // 唐辛子の粉
  scatter(g, 53, 12, { xk: 0, yk: 0.16, rx: 0.55, ry: 0.22 }, (px, py, rnd) => {
    fillPath(
      ctx,
      () => ctx.arc(px, py, u(g, 0.016), 0, TAU),
      `rgba(196, 46, 30, ${0.4 + rnd() * 0.4})`,
    );
  });
};

/* ---- L4 かしわめし: 茶碗 ---- */
const drawRiceBowl: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.9,
    rimY: -0.02,
    bottomY: 0.52,
    footHalf: 0.32,
    color: CERAMIC,
    moundH: 0.38,
    bandColor: 'rgba(44, 74, 114, 0.5)',
  };
  groundShadow(ctx, g, 0.72, 0.62);
  drawBowlFoot(ctx, g, spec);
  drawBowlBody(ctx, g, spec);

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    // ごはんの山
    const grad = ctx.createLinearGradient(0, y(g, -0.36), 0, y(g, 0.14));
    grad.addColorStop(0, lighten(g.base, 0.45));
    grad.addColorStop(1, darken(g.base, 0.2));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, g.s, g.s);
    // 米粒
    scatter(g, 41, 26, { xk: 0, yk: -0.1, rx: 0.6, ry: 0.24 }, (px, py, rnd) => {
      fillPath(
        ctx,
        () => ctx.ellipse(px, py, u(g, 0.055), u(g, 0.03), rnd() * TAU, 0, TAU),
        `rgba(255, 248, 226, ${0.3 + rnd() * 0.4})`,
      );
    });
    // 鶏肉
    scatter(g, 67, 7, { xk: 0, yk: -0.12, rx: 0.5, ry: 0.2 }, (px, py, rnd) => {
      fillPath(
        ctx,
        () => roundRectPath(ctx, px, py, u(g, 0.2), u(g, 0.13), u(g, 0.05)),
        rnd() > 0.5 ? g.accent : lighten(g.accent, 0.24),
      );
    });
    // 錦糸卵
    for (let i = 0; i < 5; i += 1) {
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, -0.5 + i * 0.24), y(g, -0.3));
          ctx.quadraticCurveTo(
            x(g, -0.38 + i * 0.24),
            y(g, -0.16),
            x(g, -0.46 + i * 0.24),
            y(g, 0),
          );
        },
        'rgba(246, 206, 92, 0.85)',
        u(g, 0.035),
      );
    }
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.42, 0.16, 0.16, -1.2, 0.5);
};

/* ---- L5 ごぼう天うどん: 丼 ---- */
const drawNoodleBowl: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.96,
    rimY: -0.08,
    bottomY: 0.54,
    footHalf: 0.34,
    color: CERAMIC,
    moundH: 0.3,
    bandColor: 'rgba(44, 74, 114, 0.55)',
  };
  groundShadow(ctx, g, 0.76, 0.64);
  drawBowlFoot(ctx, g, spec);
  drawBowlBody(ctx, g, spec);

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    // つゆ（博多の澄んだ黄金色）
    const broth = ctx.createRadialGradient(
      g.cx,
      y(g, -0.06),
      u(g, 0.06),
      g.cx,
      y(g, 0.12),
      u(g, 0.9),
    );
    broth.addColorStop(0, '#dcae66');
    broth.addColorStop(1, '#9c6f2d');
    ctx.fillStyle = broth;
    ctx.fillRect(0, 0, g.s, g.s);
    // うどん
    for (let i = 0; i < 4; i += 1) {
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, -0.66), y(g, -0.12 + i * 0.1));
          ctx.bezierCurveTo(
            x(g, -0.2),
            y(g, -0.28 + i * 0.09),
            x(g, 0.2),
            y(g, 0.02 + i * 0.09),
            x(g, 0.66),
            y(g, -0.14 + i * 0.1),
          );
        },
        i % 2 === 0 ? '#fbf6ea' : '#eadfc8',
        u(g, 0.16),
      );
    }
    // ごぼう天
    for (const t of [
      { dx: -0.18, dy: -0.2, a: -0.5 },
      { dx: 0.12, dy: -0.08, a: -0.28 },
    ]) {
      ctx.save();
      ctx.translate(x(g, t.dx), y(g, t.dy));
      ctx.rotate(t.a);
      const w = u(g, 1.0);
      const h = u(g, 0.24);
      const crisp = ctx.createLinearGradient(0, -h / 2, 0, h / 2);
      crisp.addColorStop(0, '#f0c47c');
      crisp.addColorStop(0.55, '#d99a4a');
      crisp.addColorStop(1, '#a86c26');
      fillPath(ctx, () => roundRectPath(ctx, -w / 2, -h / 2, w, h, h * 0.5), crisp);
      strokePath(
        ctx,
        () => roundRectPath(ctx, -w / 2, -h / 2, w, h, h * 0.5),
        'rgba(110, 66, 20, 0.85)',
        u(g, 0.024),
      );
      const rnd = makeRandom(7);
      for (let i = 0; i < 12; i += 1) {
        fillPath(
          ctx,
          () =>
            ctx.arc(
              -w / 2 + w * (0.08 + rnd() * 0.84),
              -h * 0.22 + rnd() * h * 0.55,
              u(g, 0.03),
              0,
              TAU,
            ),
          rnd() > 0.45 ? 'rgba(255, 231, 178, 0.75)' : 'rgba(150, 92, 32, 0.7)',
        );
      }
      ctx.restore();
    }
    // ねぎ
    scatter(g, 83, 8, { xk: 0, yk: 0.02, rx: 0.55, ry: 0.16 }, (px, py) => {
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.045), 0, TAU), '#8fbe5a');
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.02), 0, TAU), 'rgba(240, 255, 226, 0.8)');
    });
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.46, 0.18, 0.18, -1.2, 0.45);
};

/* ---- L6 鉄鍋餃子: 鉄鍋 ---- */
const drawDumpling: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.9,
    rimY: 0.06,
    bottomY: 0.48,
    footHalf: 0.54,
    color: IRON,
    moundH: 0.24,
    flatten: 0.3,
  };
  groundShadow(ctx, g, 0.76, 0.6);
  drawLugs(ctx, g, spec, darken(IRON, 0.25), 0.04);
  drawBowlBody(ctx, g, spec);

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    ctx.fillStyle = darken(IRON, 0.55);
    ctx.fillRect(0, 0, g.s, g.s);
    // 餃子を放射状に並べる（鉄鍋の定番の盛り）
    const count = 5;
    for (let i = 0; i < count; i += 1) {
      const a = (i / count) * TAU - Math.PI / 2 + 0.2;
      ctx.save();
      ctx.translate(x(g, Math.cos(a) * 0.36), y(g, 0.02 + Math.sin(a) * 0.14));
      ctx.rotate(a + Math.PI / 2);
      const w = u(g, 0.62);
      const h = u(g, 0.26);
      const grad = ctx.createLinearGradient(0, -h, 0, h * 0.8);
      grad.addColorStop(0, lighten(g.base, 0.5));
      grad.addColorStop(0.55, g.base);
      grad.addColorStop(1, darken(g.accent, 0.25));
      const shape: Path = () => {
        ctx.moveTo(-w / 2, h * 0.36);
        ctx.quadraticCurveTo(0, -h * 1.25, w / 2, h * 0.36);
        ctx.quadraticCurveTo(0, h * 0.9, -w / 2, h * 0.36);
        ctx.closePath();
      };
      fillPath(ctx, shape, grad);
      strokePath(ctx, shape, darken(g.accent, 0.45), u(g, 0.024));
      // 焼き目
      fillPath(
        ctx,
        () => ctx.ellipse(0, h * 0.3, w * 0.36, h * 0.14, 0, 0, TAU),
        'rgba(126, 66, 22, 0.7)',
      );
      // ひだ
      for (let k = -1; k <= 1; k += 1) {
        strokePath(
          ctx,
          () => {
            ctx.moveTo(k * w * 0.2, h * 0.3);
            ctx.lineTo(k * w * 0.15, -h * 0.55);
          },
          'rgba(146, 92, 40, 0.6)',
          u(g, 0.022),
        );
      }
      // 皮のツヤ
      fillPath(
        ctx,
        () => ctx.ellipse(-w * 0.12, -h * 0.42, w * 0.16, h * 0.12, -0.2, 0, TAU),
        'rgba(255, 246, 224, 0.5)',
      );
      ctx.restore();
    }
    // 中央のねぎ
    fillPath(ctx, () => ctx.arc(g.cx, y(g, 0.02), u(g, 0.09), 0, TAU), '#8fbe5a');
    fillPath(ctx, () => ctx.arc(g.cx, y(g, 0.02), u(g, 0.04), 0, TAU), 'rgba(244,255,232,0.9)');
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.4, 0.24, 0.2, -1.2, 0.34);
};

/* ---- L7 焼きカレー: 耳付きグラタン皿 ---- */
const drawCurryPlate: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.8, 0.66);

  const dish: Path = () => ctx.ellipse(g.cx, y(g, 0.1), u(g, 0.9), u(g, 0.46), 0, 0, TAU);
  const inner: Path = () => ctx.ellipse(g.cx, y(g, 0.06), u(g, 0.72), u(g, 0.33), 0, 0, TAU);

  // 耳
  for (const side of [-1, 1]) {
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, 0.96 * side), y(g, 0.1), u(g, 0.14), u(g, 0.1), 0, 0, TAU),
      darken(CERAMIC, 0.12),
    );
  }

  const grad = ctx.createLinearGradient(0, y(g, -0.34), 0, y(g, 0.54));
  grad.addColorStop(0, lighten(CERAMIC, 0.3));
  grad.addColorStop(1, darken(CERAMIC, 0.34));
  withDropShadow(ctx, g, () => fillPath(ctx, dish, grad));
  strokePath(ctx, dish, darken(CERAMIC, 0.5), u(g, 0.032));

  clipped(ctx, inner, () => {
    // カレー
    ctx.fillStyle = darken(g.base, 0.28);
    ctx.fillRect(0, 0, g.s, g.s);
    // チーズ
    const cheese = ctx.createLinearGradient(0, y(g, -0.16), 0, y(g, 0.38));
    cheese.addColorStop(0, lighten(g.accent, 0.28));
    cheese.addColorStop(1, darken(g.accent, 0.12));
    fillPath(
      ctx,
      () => {
        ctx.moveTo(x(g, -0.58), y(g, 0.1));
        ctx.bezierCurveTo(x(g, -0.44), y(g, -0.2), x(g, 0.2), y(g, -0.22), x(g, 0.44), y(g, -0.02));
        ctx.bezierCurveTo(x(g, 0.62), y(g, 0.14), x(g, 0.2), y(g, 0.32), x(g, -0.2), y(g, 0.3));
        ctx.bezierCurveTo(x(g, -0.46), y(g, 0.28), x(g, -0.62), y(g, 0.2), x(g, -0.58), y(g, 0.1));
        ctx.closePath();
      },
      cheese,
    );
    // 焦げ目
    scatter(g, 97, 10, { xk: -0.04, yk: 0.06, rx: 0.4, ry: 0.16 }, (px, py, rnd) => {
      fillPath(
        ctx,
        () =>
          ctx.ellipse(px, py, u(g, 0.06 + rnd() * 0.07), u(g, 0.04 + rnd() * 0.03), rnd(), 0, TAU),
        `rgba(140, 72, 24, ${0.3 + rnd() * 0.35})`,
      );
    });
    // 半熟卵
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, 0.16), y(g, 0.12), u(g, 0.18), u(g, 0.13), 0, 0, TAU),
      '#fff3d8',
    );
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, 0.16), y(g, 0.12), u(g, 0.1), u(g, 0.075), 0, 0, TAU),
      '#f2b33d',
    );
    // パセリ
    scatter(g, 103, 8, { xk: 0, yk: 0.06, rx: 0.42, ry: 0.16 }, (px, py) => {
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.022), 0, TAU), '#5f8f3a');
    });
  });

  drawGlaze(ctx, g, dish);
  drawSpecular(ctx, g, -0.34, -0.08, 0.22, -0.3, 0.5);
};

/* ---- L8 ごまさば: 角皿 ---- */
const drawFish: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.78, 0.64);

  const half = 0.84;
  const topY = -0.16;
  const botY = 0.5;
  const tray: Path = () =>
    roundRectPath(ctx, x(g, -half), y(g, topY), u(g, half * 2), u(g, botY - topY), u(g, 0.12));
  const inner: Path = () =>
    roundRectPath(
      ctx,
      x(g, -half + 0.08),
      y(g, topY + 0.07),
      u(g, (half - 0.08) * 2),
      u(g, botY - topY - 0.14),
      u(g, 0.08),
    );

  const grad = ctx.createLinearGradient(0, y(g, topY), 0, y(g, botY));
  grad.addColorStop(0, lighten(INDIGO, 0.28));
  grad.addColorStop(1, darken(INDIGO, 0.45));
  withDropShadow(ctx, g, () => fillPath(ctx, tray, grad));
  strokePath(ctx, tray, darken(INDIGO, 0.6), u(g, 0.03));
  fillPath(ctx, inner, darken(INDIGO, 0.28));

  clipped(ctx, inner, () => {
    // 大葉
    fillPath(
      ctx,
      () => {
        ctx.moveTo(x(g, -0.64), y(g, 0.26));
        ctx.quadraticCurveTo(x(g, -0.1), y(g, -0.02), x(g, 0.6), y(g, 0.14));
        ctx.quadraticCurveTo(x(g, -0.1), y(g, 0.42), x(g, -0.64), y(g, 0.26));
        ctx.closePath();
      },
      '#4a7c34',
    );
    // 切り身3枚
    for (let i = 0; i < 3; i += 1) {
      const dx = -0.4 + i * 0.4;
      ctx.save();
      ctx.translate(x(g, dx), y(g, 0.16 - i * 0.02));
      ctx.rotate(-0.16 + i * 0.1);
      const w = u(g, 0.52);
      const h = u(g, 0.34);
      const flesh = ctx.createLinearGradient(0, -h / 2, 0, h / 2);
      flesh.addColorStop(0, '#c9736c');
      flesh.addColorStop(0.42, '#e79c8b');
      flesh.addColorStop(1, '#f4c9b6');
      fillPath(ctx, () => roundRectPath(ctx, -w / 2, -h / 2, w, h, u(g, 0.05)), flesh);
      // 皮（青銀）
      const skin = ctx.createLinearGradient(0, -h / 2, 0, -h / 2 + h * 0.36);
      skin.addColorStop(0, '#2a3f63');
      skin.addColorStop(0.55, '#5d7ea6');
      skin.addColorStop(1, '#22364f');
      fillPath(ctx, () => roundRectPath(ctx, -w / 2, -h / 2, w, h * 0.36, u(g, 0.05)), skin);
      // 血合い（皮のすぐ下の濃い赤）で「刺身」らしさを出す
      fillPath(
        ctx,
        () => roundRectPath(ctx, -w / 2, -h / 2 + h * 0.34, w, h * 0.14, u(g, 0.02)),
        'rgba(150, 44, 44, 0.55)',
      );
      strokePath(
        ctx,
        () => {
          ctx.moveTo(-w / 2 + u(g, 0.03), -h / 2 + h * 0.12);
          ctx.lineTo(w / 2 - u(g, 0.03), -h / 2 + h * 0.1);
        },
        'rgba(230, 244, 255, 0.7)',
        u(g, 0.02),
      );
      // 身の照り
      fillPath(
        ctx,
        () => ctx.ellipse(-w * 0.14, h * 0.2, w * 0.22, h * 0.09, -0.2, 0, TAU),
        'rgba(255, 255, 255, 0.2)',
      );
      strokePath(
        ctx,
        () => roundRectPath(ctx, -w / 2, -h / 2, w, h, u(g, 0.05)),
        'rgba(90, 40, 40, 0.4)',
        u(g, 0.018),
      );
      ctx.restore();
    }
    // ごま
    scatter(g, 113, 16, { xk: 0, yk: 0.14, rx: 0.55, ry: 0.16 }, (px, py, rnd) => {
      fillPath(
        ctx,
        () => ctx.ellipse(px, py, u(g, 0.022), u(g, 0.014), rnd() * TAU, 0, TAU),
        rnd() > 0.5 ? 'rgba(255,255,255,0.9)' : 'rgba(60,44,30,0.8)',
      );
    });
  });

  drawGlaze(ctx, g, tray);
  drawSpecular(ctx, g, -0.44, 0.08, 0.2, -0.1, 0.4);
};

/* ---- L9 博多豚骨ラーメン: ラーメン鉢 ---- */
const drawRamen: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.98,
    rimY: -0.1,
    bottomY: 0.52,
    footHalf: 0.34,
    color: '#f6f1e6',
    moundH: 0.3,
    bandColor: 'rgba(184, 69, 47, 0.75)',
  };
  groundShadow(ctx, g, 0.78, 0.62);
  drawBowlFoot(ctx, g, spec);
  drawBowlBody(ctx, g, spec);

  // 雷文（縁の模様）
  clipped(ctx, bowlBody(ctx, g, spec), () => {
    const band = y(g, spec.rimY) + u(g, 0.07);
    for (let i = -4; i <= 4; i += 1) {
      strokePath(
        ctx,
        () => {
          const bx = x(g, i * 0.22);
          ctx.moveTo(bx, band + u(g, 0.02));
          ctx.lineTo(bx + u(g, 0.08), band + u(g, 0.02));
          ctx.lineTo(bx + u(g, 0.08), band + u(g, 0.08));
        },
        'rgba(255, 240, 226, 0.7)',
        u(g, 0.022),
      );
    }
  });

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    // 白湯スープ
    const soup = ctx.createRadialGradient(g.cx, y(g, -0.1), u(g, 0.1), g.cx, y(g, 0.1), u(g, 0.9));
    soup.addColorStop(0, lighten(g.base, 0.4));
    soup.addColorStop(1, darken(g.base, 0.14));
    ctx.fillStyle = soup;
    ctx.fillRect(0, 0, g.s, g.s);
    // 細麺
    for (let i = 0; i < 5; i += 1) {
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, -0.6), y(g, -0.16 + i * 0.07));
          ctx.bezierCurveTo(
            x(g, -0.2),
            y(g, -0.26 + i * 0.07),
            x(g, 0.2),
            y(g, -0.04 + i * 0.07),
            x(g, 0.6),
            y(g, -0.18 + i * 0.07),
          );
        },
        i % 2 === 0 ? '#f7e3ae' : '#eed398',
        u(g, 0.05),
      );
    }
    // チャーシュー
    for (const p of [
      { dx: -0.34, dy: -0.02 },
      { dx: -0.06, dy: 0.06 },
    ]) {
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.22), u(g, 0.15), -0.2, 0, TAU),
        '#c98a63',
      );
      strokePath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.22), u(g, 0.15), -0.2, 0, TAU),
        '#8f5636',
        u(g, 0.022),
      );
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy - 0.05), u(g, 0.16), u(g, 0.04), -0.2, 0, TAU),
        'rgba(250, 236, 224, 0.75)',
      );
    }
    // 海苔
    fillPath(
      ctx,
      () => roundRectPath(ctx, x(g, 0.26), y(g, -0.26), u(g, 0.3), u(g, 0.4), u(g, 0.03)),
      '#2a2b26',
    );
    strokePath(
      ctx,
      () => {
        ctx.moveTo(x(g, 0.3), y(g, -0.2));
        ctx.lineTo(x(g, 0.52), y(g, -0.16));
      },
      'rgba(255,255,255,0.18)',
      u(g, 0.02),
    );
    // ねぎ・紅生姜
    scatter(g, 127, 10, { xk: -0.1, yk: 0.02, rx: 0.48, ry: 0.16 }, (px, py) => {
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.035), 0, TAU), '#7fb350');
    });
    scatter(g, 131, 6, { xk: 0.3, yk: 0.1, rx: 0.2, ry: 0.1 }, (px, py) => {
      fillPath(ctx, () => ctx.ellipse(px, py, u(g, 0.06), u(g, 0.018), 0.4, 0, TAU), g.accent);
    });
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.48, 0.14, 0.2, -1.2, 0.42);
};

/* ---- L10 水炊き: 土鍋 ---- */
const drawPot: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.9,
    rimY: -0.06,
    bottomY: 0.5,
    footHalf: 0.5,
    color: CLAY,
    moundH: 0.3,
    flatten: 0.28,
  };
  groundShadow(ctx, g, 0.78, 0.6);
  drawLugs(ctx, g, spec, darken(CLAY, 0.2), -0.04);
  drawBowlBody(ctx, g, spec);

  // 土鍋の白い口紐
  clipped(ctx, bowlBody(ctx, g, spec), () => {
    ctx.fillStyle = 'rgba(238, 230, 214, 0.85)';
    ctx.fillRect(0, y(g, spec.rimY) + u(g, 0.04), g.s, u(g, 0.09));
  });

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    // 白濁スープ（水炊きの命。器の色に引きずられないよう白を直接指定する）
    const soup = ctx.createRadialGradient(
      g.cx,
      y(g, -0.08),
      u(g, 0.06),
      g.cx,
      y(g, 0.1),
      u(g, 0.8),
    );
    soup.addColorStop(0, '#fdf9f0');
    soup.addColorStop(0.55, '#f0e6d4');
    soup.addColorStop(1, '#d8c8ae');
    ctx.fillStyle = soup;
    ctx.fillRect(0, 0, g.s, g.s);
    // 湯だった表面の照り
    fillPath(
      ctx,
      () => ctx.ellipse(x(g, -0.2), y(g, -0.1), u(g, 0.34), u(g, 0.1), -0.16, 0, TAU),
      'rgba(255, 255, 255, 0.45)',
    );
    // キャベツ・白菜
    for (const p of [
      { dx: -0.36, dy: -0.06, a: -0.3 },
      { dx: 0.3, dy: -0.02, a: 0.34 },
    ]) {
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.26), u(g, 0.16), p.a, 0, TAU),
        lighten(g.accent, 0.42),
      );
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, p.dx - 0.16), y(g, p.dy + 0.04));
          ctx.quadraticCurveTo(x(g, p.dx), y(g, p.dy - 0.1), x(g, p.dx + 0.16), y(g, p.dy + 0.02));
        },
        darken(g.accent, 0.1),
        u(g, 0.026),
      );
    }
    // 鶏だんご
    for (const p of [
      { dx: -0.06, dy: 0.06 },
      { dx: 0.06, dy: -0.14 },
      { dx: -0.24, dy: 0.12 },
    ]) {
      const grad = ctx.createRadialGradient(
        x(g, p.dx - 0.04),
        y(g, p.dy - 0.05),
        u(g, 0.01),
        x(g, p.dx),
        y(g, p.dy),
        u(g, 0.2),
      );
      grad.addColorStop(0, '#fdf4e2');
      grad.addColorStop(1, '#e2cba6');
      fillPath(ctx, () => ctx.arc(x(g, p.dx), y(g, p.dy), u(g, 0.15), 0, TAU), grad);
      strokePath(
        ctx,
        () => ctx.arc(x(g, p.dx), y(g, p.dy), u(g, 0.15), 0, TAU),
        'rgba(150, 120, 82, 0.5)',
        u(g, 0.02),
      );
    }
    // 青ねぎ
    scatter(g, 149, 9, { xk: 0, yk: 0.04, rx: 0.5, ry: 0.16 }, (px, py) => {
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.04), 0, TAU), '#7cb04c');
      fillPath(ctx, () => ctx.arc(px, py, u(g, 0.017), 0, TAU), 'rgba(245,255,235,0.85)');
    });
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.42, 0.2, 0.18, -1.2, 0.36);
};

/* ---- L11 もつ鍋: 浅い鉄鍋（にら山盛り） ---- */
const drawHotpot: Drawer = (ctx, g) => {
  const spec: BowlSpec = {
    rimHalf: 0.94,
    rimY: 0.06,
    bottomY: 0.5,
    footHalf: 0.58,
    color: IRON,
    moundH: 0.38,
    flatten: 0.3,
  };
  groundShadow(ctx, g, 0.8, 0.6);
  drawLugs(ctx, g, spec, darken(IRON, 0.3), 0.06);
  drawBowlBody(ctx, g, spec);

  clipped(ctx, bowlInner(ctx, g, spec), () => {
    // 赤いスープ
    const soup = ctx.createLinearGradient(0, y(g, -0.2), 0, y(g, 0.3));
    soup.addColorStop(0, lighten(g.base, 0.14));
    soup.addColorStop(1, darken(g.base, 0.34));
    ctx.fillStyle = soup;
    ctx.fillRect(0, 0, g.s, g.s);
    // キャベツ
    for (const p of [
      { dx: -0.4, dy: 0.0 },
      { dx: 0.36, dy: 0.02 },
    ]) {
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.24), u(g, 0.14), 0.2, 0, TAU),
        '#e7e6c8',
      );
    }
    // もつ
    for (const p of [
      { dx: -0.12, dy: 0.08 },
      { dx: 0.12, dy: 0.02 },
      { dx: 0.0, dy: -0.06 },
    ]) {
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.13), u(g, 0.09), 0.3, 0, TAU),
        '#f0dcc0',
      );
      strokePath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.13), u(g, 0.09), 0.3, 0, TAU),
        'rgba(150, 96, 62, 0.6)',
        u(g, 0.02),
      );
    }
    // にらの山
    const rnd = makeRandom(163);
    for (let i = 0; i < 26; i += 1) {
      const dx = -0.62 + rnd() * 1.24;
      const top = -0.34 + rnd() * 0.2;
      strokePath(
        ctx,
        () => {
          ctx.moveTo(x(g, dx), y(g, 0.14));
          ctx.quadraticCurveTo(x(g, dx + 0.06), y(g, top + 0.1), x(g, dx - 0.04), y(g, top));
        },
        rnd() > 0.5 ? g.accent : lighten(g.accent, 0.26),
        u(g, 0.05),
      );
    }
    // 唐辛子
    for (const p of [
      { dx: -0.2, dy: -0.16 },
      { dx: 0.26, dy: -0.1 },
    ]) {
      fillPath(
        ctx,
        () => ctx.ellipse(x(g, p.dx), y(g, p.dy), u(g, 0.11), u(g, 0.035), -0.5, 0, TAU),
        '#cf3b26',
      );
    }
  });

  drawInnerShade(ctx, g, spec);
  drawBowlRim(ctx, g, spec);
  drawGlaze(ctx, g, bowlBody(ctx, g, spec));
  drawSpecular(ctx, g, -0.44, 0.24, 0.2, -1.2, 0.3);
};

/* ---- L12 福岡うまいもの大集合: 折敷（お膳） ---- */
const drawFeast: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.84, 0.68);

  const half = 0.9;
  const topY = 0.0;
  const botY = 0.54;
  const tray: Path = () =>
    roundRectPath(ctx, x(g, -half), y(g, topY), u(g, half * 2), u(g, botY - topY), u(g, 0.1));

  const grad = ctx.createLinearGradient(0, y(g, topY), 0, y(g, botY));
  grad.addColorStop(0, lighten(LACQUER, 0.3));
  grad.addColorStop(1, darken(LACQUER, 0.45));
  withDropShadow(ctx, g, () => fillPath(ctx, tray, grad));
  strokePath(ctx, tray, lighten(g.base, 0.1), u(g, 0.036));

  // 膳の上の小鉢（3つ）
  const dish = (dx: number, dy: number, rr: number, bowlColor: string, fill: string) => {
    const bowl: BowlSpec = {
      rimHalf: rr,
      rimY: dy,
      bottomY: dy + rr * 0.66,
      footHalf: rr * 0.42,
      color: bowlColor,
      moundH: rr * 0.34,
      flatten: 0.3,
    };
    const shifted: Geo = { ...g, cx: x(g, dx) };
    drawBowlBody(ctx, shifted, bowl);
    clipped(ctx, bowlInner(ctx, shifted, bowl), () => {
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, g.s, g.s);
      scatter(shifted, 173, 6, { xk: 0, yk: dy - 0.02, rx: rr * 0.5, ry: rr * 0.2 }, (px, py) => {
        fillPath(ctx, () => ctx.arc(px, py, u(g, rr * 0.1), 0, TAU), 'rgba(255,255,255,0.45)');
      });
    });
    drawBowlRim(ctx, shifted, bowl);
    drawGlaze(ctx, shifted, bowlBody(ctx, shifted, bowl));
  };

  dish(-0.48, -0.02, 0.36, CERAMIC, '#d9503f');
  dish(0.46, 0.0, 0.34, INDIGO, '#f2e6c9');
  dish(-0.02, -0.18, 0.42, '#f6f1e6', '#e8c85c');

  // 箸
  for (let i = 0; i < 2; i += 1) {
    strokePath(
      ctx,
      () => {
        ctx.moveTo(x(g, -0.72), y(g, 0.42 + i * 0.06));
        ctx.lineTo(x(g, 0.54), y(g, 0.36 + i * 0.06));
      },
      i === 0 ? darken(LACQUER, 0.65) : darken(LACQUER, 0.5),
      u(g, 0.035),
    );
  }

  // 金の縁飾り
  strokePath(
    ctx,
    () =>
      roundRectPath(
        ctx,
        x(g, -half + 0.06),
        y(g, topY + 0.05),
        u(g, (half - 0.06) * 2),
        u(g, botY - topY - 0.1),
        u(g, 0.08),
      ),
    'rgba(242, 193, 78, 0.6)',
    u(g, 0.02),
  );

  drawSpecular(ctx, g, -0.5, 0.14, 0.2, -0.1, 0.34);
};

/* ---- L12 玄界灘のとらふぐ てっさ: 大皿の菊花盛り ---- */
const drawSashimiPlatter: Drawer = (ctx, g) => {
  groundShadow(ctx, g, 0.92, 0.62);

  const cy = 0.02;
  const plateRx = 0.98;
  const plateRy = 0.58;
  const plate: Path = () => ctx.ellipse(g.cx, y(g, cy), u(g, plateRx), u(g, plateRy), 0, 0, TAU);

  // 皿の縁の厚み（真上ではなく少し斜めから見ているので下側に見える）
  fillPath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, cy + 0.07), u(g, plateRx), u(g, plateRy), 0, 0, TAU),
    darken(g.base, 0.45),
  );

  // 皿の面
  const face = ctx.createLinearGradient(0, y(g, cy - plateRy), 0, y(g, cy + plateRy));
  face.addColorStop(0, lighten(g.base, 0.5));
  face.addColorStop(0.45, g.base);
  face.addColorStop(1, darken(g.base, 0.24));
  withDropShadow(ctx, g, () => fillPath(ctx, plate, face));

  // 金の縁取り（最高級の一皿らしさ）
  strokePath(ctx, plate, 'rgba(214, 172, 84, 0.85)', u(g, 0.035));
  strokePath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, cy), u(g, plateRx * 0.88), u(g, plateRy * 0.86), 0, 0, TAU),
    'rgba(214, 172, 84, 0.35)',
    u(g, 0.018),
  );

  /**
   * ふぐの薄造り。
   * 皿の模様が透けるほど薄いのが身上なので、白の半透明の花びらを
   * 外側から内側へ二重の輪に並べ、縁だけ淡い青灰でしめる。
   */
  const petal = (angle: number, ringRx: number, ringRy: number, size: number, alpha: number) => {
    const px = g.cx + Math.cos(angle) * u(g, ringRx);
    const py = y(g, cy) + Math.sin(angle) * u(g, ringRy);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle + Math.PI / 2);
    ctx.scale(1, 0.62);
    fillPath(
      ctx,
      () => ctx.ellipse(0, 0, u(g, size), u(g, size * 0.52), 0, 0, TAU),
      `rgba(255, 252, 248, ${alpha})`,
    );
    strokePath(
      ctx,
      () => ctx.ellipse(0, 0, u(g, size), u(g, size * 0.52), 0, 0, TAU),
      'rgba(168, 190, 208, 0.55)',
      u(g, 0.016),
    );
    // 身の照り
    fillPath(
      ctx,
      () =>
        ctx.ellipse(
          -u(g, size * 0.22),
          -u(g, size * 0.16),
          u(g, size * 0.34),
          u(g, size * 0.12),
          -0.4,
          0,
          TAU,
        ),
      'rgba(255, 255, 255, 0.7)',
    );
    ctx.restore();
  };

  const outer = 18;
  for (let i = 0; i < outer; i += 1) {
    const a = (TAU * i) / outer - Math.PI / 2;
    petal(a, plateRx * 0.66, plateRy * 0.62, 0.3, 0.82);
  }
  const inner = 12;
  for (let i = 0; i < inner; i += 1) {
    const a = (TAU * i) / inner - Math.PI / 2 + Math.PI / inner;
    petal(a, plateRx * 0.36, plateRy * 0.34, 0.25, 0.9);
  }

  // 中央の薬味（青ねぎともみじおろし）
  fillPath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, cy), u(g, 0.2), u(g, 0.12), 0, 0, TAU),
    'rgba(255, 253, 250, 0.95)',
  );
  const rnd = makeRandom(4212);
  for (let i = 0; i < 12; i += 1) {
    const a = rnd() * TAU;
    const rr = rnd() * 0.15;
    strokePath(
      ctx,
      () => {
        ctx.moveTo(g.cx + Math.cos(a) * u(g, rr), y(g, cy) + Math.sin(a) * u(g, rr * 0.6));
        ctx.lineTo(
          g.cx + Math.cos(a) * u(g, rr) + u(g, 0.05),
          y(g, cy) + Math.sin(a) * u(g, rr * 0.6) - u(g, 0.015),
        );
      },
      '#5f9a3c',
      u(g, 0.03),
    );
  }
  fillPath(
    ctx,
    () => ctx.ellipse(g.cx, y(g, cy + 0.01), u(g, 0.075), u(g, 0.045), 0, 0, TAU),
    g.accent,
  );

  // 大葉（奥に2枚。緑を効かせて白一色になるのを防ぐ）
  for (const side of [-1, 1]) {
    fillPath(
      ctx,
      () =>
        ctx.ellipse(x(g, 0.62 * side), y(g, cy - 0.36), u(g, 0.24), u(g, 0.11), 0.5 * side, 0, TAU),
      '#417f36',
    );
    strokePath(
      ctx,
      () => {
        ctx.moveTo(x(g, 0.62 * side - 0.2 * side), y(g, cy - 0.37));
        ctx.lineTo(x(g, 0.62 * side + 0.2 * side), y(g, cy - 0.35));
      },
      'rgba(226, 245, 214, 0.6)',
      u(g, 0.018),
    );
  }

  drawGlaze(ctx, g, plate);
  drawSpecular(ctx, g, -0.46, cy - 0.3, 0.22, -0.25, 0.4);
};

const DRAWERS: Record<IconShape, Drawer> = {
  berry: drawBerry,
  mochi: drawMochi,
  roe: drawRoe,
  'rice-bowl': drawRiceBowl,
  'noodle-bowl': drawNoodleBowl,
  dumpling: drawDumpling,
  'curry-plate': drawCurryPlate,
  fish: drawFish,
  ramen: drawRamen,
  pot: drawPot,
  hotpot: drawHotpot,
  feast: drawFeast,
  'sashimi-platter': drawSashimiPlatter,
};

/* ------------------------------ 当たり判定へのフィット ------------------------------ */

/**
 * 描いた絵が当たり判定の円をどれだけ埋めるか（1.0 = 直径いっぱい）。
 *
 * 器に盛られた料理はどうしても横長になり、そのままだと縦方向に3〜4割の
 * 余白ができる。すると積み上げたときに「くっついていないのに止まっている」
 * ように見え、宙に浮いているとしか思えない挙動になってしまう。
 * 描画後に実寸を測って円へフィットさせることで、この違和感を消す。
 */
const ICON_FILL = 0.99;
/** 縦方向だけ追加で引き伸ばせる上限（真上から見た皿に近づけて余白を潰す）。 */
const ICON_MAX_STRETCH = 1.34;
/** 全体の拡大上限（極端に小さい絵が破綻しないように）。 */
const ICON_MAX_SCALE = 1.5;

interface ArtBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 描画済みキャンバスから、実際に絵が存在する範囲を求める。 */
function measureArtBounds(canvas: HTMLCanvasElement, threshold = 24): ArtBounds | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const { width: w, height: h } = canvas;
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null;
  }
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let py = 0; py < h; py += 1) {
    const row = py * w;
    for (let px = 0; px < w; px += 1) {
      if (data[(row + px) * 4 + 3] <= threshold) continue;
      if (px < x0) x0 = px;
      if (px > x1) x1 = px;
      if (py < y0) y0 = py;
      if (py > y1) y1 = py;
    }
  }
  if (x1 < 0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * 料理の絵を当たり判定の円へフィットさせて描く。
 *
 * 1. いったん別キャンバスへ描く
 * 2. 実際に絵がある範囲を測る
 * 3. 長辺が円の直径になるよう等倍拡大し、足りない縦方向だけ少し引き伸ばす
 * 4. 円の中心へ合わせて描き戻す
 *
 * これで見た目と当たり判定のズレが小さくなり、料理どうしがきちんと
 * 接して積み上がっているように見える。
 */
function drawFittedArt(ctx: CanvasRenderingContext2D, g: Geo, drawer: Drawer): void {
  const s = g.s;
  if (typeof document === 'undefined') {
    drawer(ctx, g);
    return;
  }

  let art: HTMLCanvasElement;
  let artCtx: CanvasRenderingContext2D | null;
  try {
    art = document.createElement('canvas');
    art.width = s;
    art.height = s;
    artCtx = art.getContext('2d', { willReadFrequently: true });
  } catch {
    drawer(ctx, g);
    return;
  }
  if (!artCtx) {
    drawer(ctx, g);
    return;
  }

  artCtx.lineJoin = 'round';
  artCtx.lineCap = 'round';
  drawer(artCtx, g);

  const bounds = measureArtBounds(art);
  if (!bounds) {
    ctx.drawImage(art, 0, 0);
    return;
  }

  const target = s * ICON_FILL;
  const uniform = Math.min(ICON_MAX_SCALE, target / Math.max(bounds.w, bounds.h));
  const stretch = Math.min(ICON_MAX_STRETCH, Math.max(1, target / (bounds.h * uniform)));

  ctx.save();
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(s / 2, s / 2);
  ctx.scale(uniform, uniform * stretch);
  ctx.translate(-(bounds.x + bounds.w / 2), -(bounds.y + bounds.h / 2));
  ctx.drawImage(art, 0, 0);
  ctx.restore();
}

/* --------------------------------- 仕上げ --------------------------------- */

/**
 * 料理写真の「ライティング」を全アイコン共通で乗せる。
 *
 * 描画済みのピクセルだけを対象にしたいので source-atop を使う。
 * 左上からの暖色キーライト → 右下の影 → 屋台の提灯を思わせる下からの照り返し、
 * という順で重ねると、どの形の料理でも統一感のある「美味しそうな照り」になる。
 */
function applyPlating(ctx: CanvasRenderingContext2D, g: Geo): void {
  const s = g.s;
  ctx.save();
  ctx.globalCompositeOperation = 'source-atop';

  const key = ctx.createLinearGradient(x(g, -1), y(g, -1.05), x(g, 0.95), y(g, 1.05));
  key.addColorStop(0, 'rgba(255, 226, 168, 0.26)');
  key.addColorStop(0.34, 'rgba(255, 240, 210, 0.07)');
  key.addColorStop(0.62, 'rgba(60, 28, 14, 0.06)');
  key.addColorStop(1, 'rgba(38, 15, 6, 0.34)');
  ctx.fillStyle = key;
  ctx.fillRect(0, 0, s, s);

  // 下からの暖かい照り返し（屋台の灯り）
  const bounce = ctx.createRadialGradient(g.cx, y(g, 1.0), u(g, 0.05), g.cx, y(g, 1.0), u(g, 1.25));
  bounce.addColorStop(0, 'rgba(255, 154, 62, 0.2)');
  bounce.addColorStop(1, 'rgba(255, 154, 62, 0)');
  ctx.fillStyle = bounce;
  ctx.fillRect(0, 0, s, s);

  // 接地部の落ち込み（アンビエントオクルージョン）
  const contact = ctx.createLinearGradient(0, y(g, 0.55), 0, y(g, 1.02));
  contact.addColorStop(0, 'rgba(24, 10, 4, 0)');
  contact.addColorStop(1, 'rgba(24, 10, 4, 0.3)');
  ctx.fillStyle = contact;
  ctx.fillRect(0, 0, s, s);

  ctx.restore();
}

/**
 * 縁の光（リムライト）。
 *
 * 描いた絵のシルエットを少しずらして自分自身で削ることで、
 * 左上側にだけ細い光の三日月を残す。輪郭が締まり、
 * 暗い器の中でも料理がくっきり浮かび上がる。
 */
function applyRimLight(ctx: CanvasRenderingContext2D, g: Geo): void {
  if (typeof document === 'undefined') return;
  const s = g.s;
  const shift = Math.max(1, s * 0.016);
  try {
    const tmp = document.createElement('canvas');
    tmp.width = s;
    tmp.height = s;
    const t = tmp.getContext('2d');
    if (!t) return;

    // 1. シルエットを光の色で塗りつぶす
    t.drawImage(ctx.canvas, 0, 0);
    t.globalCompositeOperation = 'source-in';
    t.fillStyle = 'rgba(255, 238, 206, 1)';
    t.fillRect(0, 0, s, s);

    // 2. 右下へずらした自分自身で内側を削り、左上の縁だけ残す
    t.globalCompositeOperation = 'destination-out';
    t.drawImage(ctx.canvas, shift, shift);

    // 3. 左上ほど強く、右下へ向けて消えるようマスクする
    t.globalCompositeOperation = 'destination-in';
    const mask = t.createLinearGradient(0, 0, s, s);
    mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
    mask.addColorStop(0.5, 'rgba(0, 0, 0, 0.32)');
    mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
    t.fillStyle = mask;
    t.fillRect(0, 0, s, s);

    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    ctx.globalAlpha = 0.5;
    ctx.drawImage(tmp, 0, 0);
    ctx.restore();
  } catch {
    // Canvas が使えない環境では光沢なしで続行する
  }
}

/* --------------------------------- 本体 --------------------------------- */

export interface DrawIconOptions {
  /** レベル数字バッジを描くか。 */
  showLevelBadge?: boolean;
  /**
   * 料理名の短縮ラベルを重ねるか。
   * 既定は false。料理そのものを大きく見せたいので、名前は UI 側のテキストで補う。
   */
  showLabel?: boolean;
  /** 図鑑用に彩度を落とすか。 */
  dimmed?: boolean;
}

/** アイテム1つ分の仮アイコンを Canvas へ描画する（size は正方形の一辺）。 */
export function drawItemIcon(
  ctx: CanvasRenderingContext2D,
  item: StageItemDefinition,
  size: number,
  options: DrawIconOptions = {},
): void {
  const { icon, level } = item;
  const s = size;
  const g: Geo = {
    s,
    cx: s / 2,
    cy: s / 2,
    r: s * 0.475,
    base: icon.baseColor,
    accent: icon.accentColor,
  };

  ctx.clearRect(0, 0, s, s);
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  /* --- 器＋料理（形そのものが識別情報になる） --- */
  const drawer = DRAWERS[icon.shape] ?? drawBerry;
  drawFittedArt(ctx, g, drawer);

  /* --- 全アイコン共通の照り・陰影・縁の光 --- */
  applyPlating(ctx, g);
  applyRimLight(ctx, g);

  /* --- 名札（既定では描かない。必要な画面だけ明示的に有効化する） --- */
  if (options.showLabel) {
    const label = icon.label;
    const plateW = Math.min(u(g, 1.1), s * 0.53);
    const plateH = s * 0.16;
    const plateX = g.cx - plateW / 2;
    const plateY = y(g, 0.4);

    ctx.save();
    ctx.shadowColor = 'rgba(20, 10, 6, 0.45)';
    ctx.shadowBlur = u(g, 0.1);
    ctx.shadowOffsetY = u(g, 0.03);
    fillPath(
      ctx,
      () => roundRectPath(ctx, plateX, plateY, plateW, plateH, plateH * 0.34),
      'rgba(255, 251, 243, 0.96)',
    );
    ctx.restore();
    strokePath(
      ctx,
      () => roundRectPath(ctx, plateX, plateY, plateW, plateH, plateH * 0.34),
      darken(icon.accentColor, 0.25),
      s * 0.008,
    );

    const fontSize =
      label.length >= 3 ? plateH * 0.62 : label.length === 2 ? plateH * 0.74 : plateH * 0.82;
    ctx.font = `700 ${fontSize}px ${ICON_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = icon.textColor ?? '#33200f';
    ctx.fillText(label, g.cx, plateY + plateH * 0.54, plateW * 0.86);
  }

  /* --- レベルバッジ（色以外の識別手段。料理と重ならない左上へ） --- */
  if (options.showLevelBadge !== false) {
    const badgeR = s * 0.082;
    const bx = x(g, -0.52);
    const by = y(g, -0.62);
    ctx.save();
    ctx.shadowColor = 'rgba(18, 8, 3, 0.55)';
    ctx.shadowBlur = u(g, 0.07);
    fillPath(ctx, () => ctx.arc(bx, by, badgeR, 0, TAU), darken(icon.accentColor, 0.58));
    ctx.restore();
    strokePath(ctx, () => ctx.arc(bx, by, badgeR, 0, TAU), 'rgba(255, 236, 196, 0.92)', s * 0.011);
    ctx.fillStyle = '#fffaf2';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 ${badgeR * 1.24}px ${ICON_FONT}`;
    ctx.fillText(String(level), bx, by + badgeR * 0.06);
  }

  /* --- 図鑑の未開放表示。描いた部分だけを灰色に沈める --- */
  if (options.dimmed) {
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = 'rgba(122, 114, 108, 0.66)';
    ctx.fillRect(0, 0, s, s);
    ctx.globalCompositeOperation = 'source-over';
  }

  ctx.restore();
}

/** 仮アイコンの Canvas を生成する。 */
export function createIconCanvas(
  item: StageItemDefinition,
  size: number,
  options: DrawIconOptions = {},
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const resolved = Math.max(24, Math.round(size));
  canvas.width = resolved;
  canvas.height = resolved;
  const ctx = canvas.getContext('2d');
  if (ctx) drawItemIcon(ctx, item, resolved, options);
  return canvas;
}

/** DOM の <img> で使えるデータURLを生成する。 */
export function createIconDataUrl(
  item: StageItemDefinition,
  size: number,
  options: DrawIconOptions = {},
): string {
  return createIconCanvas(item, size, options).toDataURL('image/png');
}

/**
 * 仮アイコン描画に必要な解像度。
 * 小さい料理でも輪郭と照りがつぶれないよう、表示サイズの約2.5倍を確保する。
 */
export function iconRenderSize(radius: number): number {
  return Math.max(128, Math.ceil((radius * 2 * 5) / 2));
}
